import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { mockData } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Check, 
  Sparkles, 
  Zap, 
  Shield, 
  Users, 
  Globe, 
  Clock, 
  Star,
  Target,
  TrendingUp,
  Award,
  HelpCircle,
  Calculator,
  IndianRupee,
  ArrowRight,
  X,
  ChevronDown,
  ChevronUp,
  CircleDollarSign
} from 'lucide-react';

const Pricing = () => {
  const [estimatorService, setEstimatorService] = useState('website');
  const [estimatorComplexity, setEstimatorComplexity] = useState('medium');
  const [estimatorTimeline, setEstimatorTimeline] = useState('standard');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showAddons, setShowAddons] = useState(false);
  const [activeFAQ, setActiveFAQ] = useState(null);
  const [currency, setCurrency] = useState('INR');
  const [exchangeRate, setExchangeRate] = useState(83); // Default INR to USD rate

  // Convert USD to INR
  const convertToINR = (usd) => Math.round(usd * exchangeRate);
  
  // Format currency with Indian numbering
  const formatCurrency = (amount) => {
    if (currency === 'INR') {
      const inrAmount = convertToINR(amount);
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(inrAmount);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateEstimate = () => {
    const basePrice = {
      website: 5000,
      app: 15000,
      design: 3000,
      marketing: 2000,
      finance: 1500,
      'full-stack': 25000,
      'e-commerce': 12000,
      'cms': 8000
    };

    const complexityMultiplier = {
      low: 0.6,
      medium: 1,
      high: 1.8,
      'enterprise': 2.5
    };

    const timelineMultiplier = {
      rush: 1.4,
      standard: 1,
      flexible: 0.85,
      'custom': 1.2
    };

    const estimate = basePrice[estimatorService] * 
                    complexityMultiplier[estimatorComplexity] * 
                    timelineMultiplier[estimatorTimeline];

    return currency === 'INR' ? convertToINR(Math.round(estimate / 100) * 100) : Math.round(estimate / 100) * 100;
  };

  const popularAddons = [
    { 
      id: 1, 
      name: 'Priority Support', 
      price: 500, 
      period: 'month',
      description: '24/7 support with 1-hour response time',
      features: ['Dedicated account manager', 'Phone support', 'Emergency hotline']
    },
    { 
      id: 2, 
      name: 'Training & Documentation', 
      price: 1500, 
      period: 'one-time',
      description: 'Comprehensive training for your team',
      features: ['2 training sessions', 'User manuals', 'Video tutorials']
    },
    { 
      id: 3, 
      name: 'SEO Optimization', 
      price: 2000, 
      period: 'one-time',
      description: 'Complete SEO setup and optimization',
      features: ['Keyword research', 'On-page SEO', 'Technical audit']
    },
    { 
      id: 4, 
      name: 'Performance Optimization', 
      price: 1000, 
      period: 'one-time',
      description: 'Speed and performance enhancements',
      features: ['Lighthouse optimization', 'CDN setup', 'Image optimization']
    },
    { 
      id: 5, 
      name: 'Security Suite', 
      price: 800, 
      period: 'month',
      description: 'Advanced security features',
      features: ['SSL certificates', 'DDoS protection', 'Security audits']
    },
    { 
      id: 6, 
      name: 'Analytics Dashboard', 
      price: 600, 
      period: 'month',
      description: 'Advanced analytics and reporting',
      features: ['Custom dashboards', 'Real-time reports', 'Data export']
    }
  ];

  const faqs = [
    { question: 'What payment methods do you accept?', answer: 'We accept all major credit/debit cards, UPI, Net Banking, and bank transfers.' },
    { question: 'Is there a refund policy?', answer: 'Yes, we offer a 14-day money-back guarantee for all monthly plans. Custom project refunds are evaluated case-by-case.' },
    { question: 'Can I upgrade or downgrade my plan?', answer: 'Absolutely! You can change your plan at any time. Changes take effect at the start of the next billing cycle.' },
    { question: 'Do you offer discounts for startups/educational institutions?', answer: 'Yes, we offer special discounts for startups, educational institutions, and NGOs. Contact us for details.' },
    { question: 'What if I need custom features not in the plan?', answer: 'We can customize any plan to include specific features. Contact us for a custom quote.' }
  ];

  const services = [
    ...mockData.services,
    { id: 'full-stack', name: 'Full Stack Development' },
    { id: 'e-commerce', name: 'E-commerce Platform' },
    { id: 'cms', name: 'Custom CMS' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-lime via-primary-lime/90 to-blue-50/50" />
        <div className="container mx-auto max-w-6xl text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-black text-white">
              <CircleDollarSign className="w-4 h-4 mr-2" />
              Transparent Pricing
            </Badge>
            
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-black mb-6">
              Simple, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Fair Pricing</span>
            </h1>
            
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-10">
              Choose the package that fits your needs, or get a custom quote for enterprise solutions. All prices in Indian Rupees.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Currency Toggle */}
      <section className="py-8 px-4 bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center justify-center gap-4">
            <span className="text-sm font-medium text-gray-700">View prices in:</span>
            <div className="inline-flex border border-gray-300 rounded-lg p-1">
              <Button
                variant={currency === 'INR' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCurrency('INR')}
                className={currency === 'INR' ? 'bg-black text-white' : ''}
              >
                <IndianRupee className="w-4 h-4 mr-2" />
                INR
              </Button>
              <Button
                variant={currency === 'USD' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCurrency('USD')}
                className={currency === 'USD' ? 'bg-black text-white' : ''}
              >
                <span className="mr-2">$</span>
                USD
              </Button>
            </div>
            <div className="text-xs text-gray-500">
              {currency === 'INR' ? '₹1 ≈ $0.012' : '$1 ≈ ₹83'}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Card className={`border-2 relative overflow-hidden h-full flex flex-col ${
                  plan.highlighted 
                    ? 'border-blue-500 bg-gradient-to-b from-white to-blue-50 shadow-2xl scale-105' 
                    : 'border-gray-200 bg-white shadow-lg'
                }`}>
                  {plan.highlighted && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-2 text-center text-sm font-semibold">
                      <Sparkles className="w-4 h-4 inline mr-2" />
                      {plan.cta}
                    </div>
                  )}
                  
                  <CardHeader className={`text-center ${plan.highlighted ? 'pt-12' : 'pt-8'} pb-6`}>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      {plan.highlighted && <Zap className="w-5 h-5 text-blue-600" />}
                      <CardTitle className="font-serif text-2xl text-black">
                        {plan.name}
                      </CardTitle>
                    </div>
                    
                    <div className="mb-4">
                      <div className="flex items-center justify-center">
                        <span className="text-5xl font-bold text-black">
                          {formatCurrency(parseInt(plan.price.replace('$', '')))}
                        </span>
                      </div>
                      {plan.period !== 'quote' && (
                        <div className="text-gray-600 mt-2">per {plan.period}</div>
                      )}
                    </div>
                    
                    <CardDescription className="text-gray-700">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="flex-grow">
                    <div className="space-y-4 mb-6">
                      {plan.features.map((feature, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="flex items-start gap-3"
                        >
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            plan.highlighted ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <Check className={`w-3 h-3 ${plan.highlighted ? 'text-blue-600' : 'text-gray-600'}`} />
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                  
                  <CardFooter>
                    <Button 
                      className={`w-full text-base py-3 ${plan.highlighted ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white hover:from-blue-700 hover:to-cyan-600' : 'bg-black text-white hover:bg-gray-800'}`}
                      asChild
                      onClick={() => setSelectedPlan(plan)}
                    >
                      <Link to="/#contact">
                        {plan.name === 'Enterprise' ? 'Contact Sales' : 'Get Started'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Estimator */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
              <Calculator className="w-4 h-4 mr-2" />
              Interactive Tool
            </Badge>
            
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-black mb-4">
              Project Cost <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-500">Estimator</span>
            </h2>
            <p className="text-xl text-gray-600">
              Get an instant estimate for your project in {currency}
            </p>
          </motion.div>

          <Card className="bg-white border-2 border-gray-200 shadow-xl rounded-2xl overflow-hidden">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div>
                  <label className="text-base font-semibold text-gray-900 mb-3 block">
                    What service do you need?
                  </label>
                  <select
                    value={estimatorService}
                    onChange={(e) => setEstimatorService(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-base"
                  >
                    {services.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-base font-semibold text-gray-900 mb-3 block">
                    Project complexity
                  </label>
                  <select
                    value={estimatorComplexity}
                    onChange={(e) => setEstimatorComplexity(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-base"
                  >
                    <option value="low">Simple - Basic features</option>
                    <option value="medium">Medium - Standard requirements</option>
                    <option value="high">Complex - Advanced features</option>
                    <option value="enterprise">Enterprise - Custom solutions</option>
                  </select>
                </div>

                <div>
                  <label className="text-base font-semibold text-gray-900 mb-3 block">
                    Timeline preference
                  </label>
                  <select
                    value={estimatorTimeline}
                    onChange={(e) => setEstimatorTimeline(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-base"
                  >
                    <option value="rush">Rush (2-3 weeks) - +40%</option>
                    <option value="standard">Standard (4-8 weeks)</option>
                    <option value="flexible">Flexible (8+ weeks) - Save 15%</option>
                    <option value="custom">Custom timeline</option>
                  </select>
                </div>
              </div>

              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 p-8 rounded-2xl text-center text-white"
              >
                <p className="text-sm font-medium text-blue-100 mb-2">Estimated Project Cost</p>
                <motion.p 
                  key={calculateEstimate()}
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  className="text-5xl md:text-6xl font-bold mb-4"
                >
                  {formatCurrency(calculateEstimate())}
                </motion.p>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-sm text-blue-200">Service</div>
                    <div className="font-semibold">{estimatorService}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-blue-200">Complexity</div>
                    <div className="font-semibold capitalize">{estimatorComplexity}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-blue-200">Timeline</div>
                    <div className="font-semibold capitalize">{estimatorTimeline}</div>
                  </div>
                </div>
                
                <p className="text-blue-100 mb-6 max-w-md mx-auto">
                  This is a rough estimate. Final pricing may vary based on detailed requirements.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    className="bg-white text-blue-600 hover:bg-blue-50 font-semibold"
                    asChild
                  >
                    <Link to="/#contact">
                      Get Accurate Quote
                    </Link>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-2 border-white text-white hover:bg-white/10"
                    onClick={() => {
                      setEstimatorService('website');
                      setEstimatorComplexity('medium');
                      setEstimatorTimeline('standard');
                    }}
                  >
                    Reset Estimator
                  </Button>
                </div>
              </motion.div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 border-blue-200 text-blue-700">
              <Shield className="w-4 h-4 mr-2" />
              Enhance Your Plan
            </Badge>
            
            <h2 className="font-serif text-4xl font-bold text-black mb-4">
              Popular <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-500">Add-Ons</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Customize your plan with these powerful add-ons
            </p>
          </div>

          <div className="mb-8 text-center">
            <Button
              variant="outline"
              onClick={() => setShowAddons(!showAddons)}
              className="inline-flex items-center gap-2"
            >
              {showAddons ? 'Hide Add-ons' : 'Show All Add-ons'}
              {showAddons ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>

          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {(showAddons ? popularAddons : popularAddons.slice(0, 3)).map((addon, index) => (
                <motion.div
                  key={addon.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-gradient-to-b from-gray-50 to-white border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <CardTitle className="text-xl">{addon.name}</CardTitle>
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                          {addon.period === 'month' ? 'Monthly' : 'One-time'}
                        </Badge>
                      </div>
                      <div className="text-2xl font-bold text-black">
                        {currency === 'INR' ? '₹' : '$'}{addon.price}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{addon.description}</p>
                      <ul className="space-y-2">
                        {addon.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <Link to="/#contact">Add to Plan</Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-20 px-4 bg-gradient-to-r from-gray-50 to-card-lime">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white">
              <Award className="w-4 h-4 mr-2" />
              Our Guarantees
            </Badge>
            
            <h2 className="font-serif text-4xl font-bold text-black mb-4">
              Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-500">Our Pricing</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: <Shield className="w-6 h-6" />, title: 'Transparent Pricing', desc: 'No hidden fees or surprise charges' },
              { icon: <Clock className="w-6 h-6" />, title: '14-Day Refund', desc: 'Money-back guarantee on monthly plans' },
              { icon: <TrendingUp className="w-6 h-6" />, title: 'Free Migration', desc: 'Help moving from your current provider' },
              { icon: <Users className="w-6 h-6" />, title: 'Dedicated Support', desc: '24/7 customer support included' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-green-100 mb-4">
                  <div className="text-green-600">{item.icon}</div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 border-purple-200 text-purple-700">
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQ
            </Badge>
            
            <h2 className="font-serif text-4xl font-bold text-black mb-4">
              Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border border-gray-200 hover:border-purple-300 transition-colors">
                  <CardContent className="p-6">
                    <button
                      onClick={() => setActiveFAQ(activeFAQ === index ? null : index)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <span className="font-semibold text-gray-900">{faq.question}</span>
                      {activeFAQ === index ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    
                    <AnimatePresence>
                      {activeFAQ === index && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t border-gray-100"
                        >
                          <p className="text-gray-600">{faq.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Target className="w-16 h-16 text-blue-400 mx-auto mb-6" />
            
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Your Project?
            </h2>
            
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              Get in touch for a detailed proposal and personalized consultation.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white hover:from-blue-700 hover:to-cyan-600 px-8 py-6 text-lg"
                asChild
              >
                <Link to="/#contact">
                  Get Started Today
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              
               <Button 
  variant="outline" 
  className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
  asChild
>
  <a href="/#contact">
    Schedule a Call
  </a>
</Button>

            </div>

            <p className="text-sm text-gray-400 mt-8">
              All prices include GST. VAT may apply for international clients.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;